import { useEffect, useMemo, useRef } from 'react';

import {
  selectConceptGroups,
  selectConceptGroupSelections,
} from '@verantos-dev/app-state-cohort-definition';
import {
  collectAllConceptNamesFromLogicTree,
  getUnresolvedConceptNames,
  useCohortDefinitionStore,
} from '@verantos-dev/feature-analytical-variables';
import { CohortBuilderItemStatus } from '@verantos-dev/types-cohorts';
import type { AnalyticalVariable } from '@verantos-dev/types-cohorts';

export const useGetAVCriteriaStatus = () => {
  const conceptGroups = useCohortDefinitionStore(selectConceptGroups);
  const conceptGroupSelections = useCohortDefinitionStore(selectConceptGroupSelections);
  const conceptGroupSelectionsWithIssues = useMemo(
    () =>
      conceptGroupSelections.filter(
        (s) => (s.status === 'warning' || s.status === 'error') && !s.reported
      ),
    [conceptGroupSelections]
  );
  const markConceptGroupSelectionsAsReported = useCohortDefinitionStore(
    (s) => s.markConceptGroupSelectionsAsReported
  );
  const analyticalVariables = useCohortDefinitionStore(
    (s) => s.analyticalVariables
  ) as AnalyticalVariable[];

  const isConceptGroupsLoading = !conceptGroups?.length;

  const { conceptGroupIdToNameMap, conceptGroupNameToOwnerMap } = useMemo(() => {
    const idToName = new Map<string, string>();
    const nameToOwner = new Map<string, string | undefined>();
    conceptGroups?.forEach(
      (cg: { conceptGroupId: string; conceptGroupName: string; owner?: string }) => {
        idToName.set(cg.conceptGroupId, cg.conceptGroupName);
        nameToOwner.set(cg.conceptGroupName.toLowerCase(), cg.owner);
      }
    );
    return { conceptGroupIdToNameMap: idToName, conceptGroupNameToOwnerMap: nameToOwner };
  }, [conceptGroups]);

  const { avStatusMap, avErrorCountMap } = useMemo(() => {
    if (isConceptGroupsLoading)
      return {
        avStatusMap: new Map<string, CohortBuilderItemStatus[]>(),
        avErrorCountMap: new Map<string, number>(),
      };

    const statusMap = new Map<string, CohortBuilderItemStatus[]>();
    const countMap = new Map<string, number>();
    analyticalVariables?.forEach((av) => {
      const allNames = collectAllConceptNamesFromLogicTree(av.logic, conceptGroupIdToNameMap);
      const unresolved = getUnresolvedConceptNames(allNames, conceptGroupNameToOwnerMap);
      if (unresolved.length > 0) {
        statusMap.set(av.id, [CohortBuilderItemStatus.ERROR]);
        countMap.set(av.id, unresolved.length);
      }
    });
    return { avStatusMap: statusMap, avErrorCountMap: countMap };
  }, [
    isConceptGroupsLoading,
    analyticalVariables,
    conceptGroupIdToNameMap,
    conceptGroupNameToOwnerMap,
  ]);

  // Map concept group selection warnings to AV ids by matching containerName → AV name
  const avIdsWithWarnings = useMemo(() => {
    const ids = new Set<string>();
    if (!conceptGroupSelectionsWithIssues?.length || !analyticalVariables?.length) return ids;

    const warningSelections = conceptGroupSelectionsWithIssues.filter(
      (s) => s.status === 'warning'
    );
    if (warningSelections.length === 0) return ids;

    const containerNames = new Set(
      warningSelections.map((s) => s.containerName?.toLowerCase()).filter((n): n is string => !!n)
    );

    for (const av of analyticalVariables) {
      if (av.name && containerNames.has(av.name.toLowerCase())) {
        ids.add(av.id);
      }
    }
    return ids;
  }, [conceptGroupSelectionsWithIssues, analyticalVariables]);

  // Sync per-AV error counts to Zustand so downstream hooks (e.g. useAVLogicErrors)
  // can read them without duplicating the concept-group resolution logic.
  const prevCountsRef = useRef<Record<string, number>>({});
  useEffect(() => {
    const counts: Record<string, number> = {};
    avErrorCountMap.forEach((v, k) => {
      counts[k] = v;
    });
    // Only write when the map actually changed (shallow key/value compare).
    const prev = prevCountsRef.current;
    const keys = Object.keys(counts);
    const changed =
      keys.length !== Object.keys(prev).length || keys.some((k) => counts[k] !== prev[k]);
    if (changed) {
      prevCountsRef.current = counts;
      useCohortDefinitionStore.getState().setConceptGroupErrorCounts(counts);
    }
  }, [avErrorCountMap]);

  // Clear suggested actions when AV error counts transition from >0 to 0
  const prevErrorCountsForClearRef = useRef<Record<string, number>>({});
  useEffect(() => {
    const prev = prevErrorCountsForClearRef.current;
    const currentCounts: Record<string, number> = {};
    avErrorCountMap.forEach((v, k) => {
      currentCounts[k] = v;
    });

    const resolvedAvIds = Object.keys(prev).filter(
      (avId) => prev[avId] > 0 && (!currentCounts[avId] || currentCounts[avId] === 0)
    );

    for (const avId of resolvedAvIds) {
      const av = analyticalVariables?.find((a) => a.id === avId);
      if (av?.name) {
        markConceptGroupSelectionsAsReported({ containerName: av.name });
      }
    }

    prevErrorCountsForClearRef.current = currentCounts;
  }, [avErrorCountMap, analyticalVariables, markConceptGroupSelectionsAsReported]);

  const getStatus = (id: string): CohortBuilderItemStatus[] => {
    const status = avStatusMap.get(id) ?? [];
    if (avIdsWithWarnings.has(id) && !status.includes(CohortBuilderItemStatus.WARNING)) {
      return [...status, CohortBuilderItemStatus.WARNING];
    }
    return status;
  };

  const getErrorCount = (id: string): number => {
    return avErrorCountMap.get(id) ?? 0;
  };

  const hasAnyUnresolved = avStatusMap.size > 0;
  const hasAnyWarnings = avIdsWithWarnings.size > 0;

  return {
    getStatus,
    getErrorCount,
    hasAnyUnresolved,
    hasAnyWarnings,
    erroredAVCount: avStatusMap.size,
    warnedAVCount: avIdsWithWarnings.size,
    isConceptGroupsLoading,
  };
};
